import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { TimeFormat } from '../helpers/date.helper';

@Directive({
  selector: '[appTimeMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeMaskDirective),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimeMaskDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class TimeMaskDirective implements ControlValueAccessor, Validator {
  _onChange: (_: Date) => void;
  _touched: () => void;

  @Input() timeFormat: TimeFormat.Time24h | TimeFormat.Time12h = TimeFormat.Time24h;

  private static readonly TIME_MASK_LENGTH = 7;
  private static readonly TIME_EMPTY_CHAR = '-';
  private static readonly TIME_SEPARATOR = ' : ';
  private static readonly TIME_MASK = `${TimeMaskDirective.TIME_EMPTY_CHAR}${TimeMaskDirective.TIME_EMPTY_CHAR}${TimeMaskDirective.TIME_SEPARATOR}${TimeMaskDirective.TIME_EMPTY_CHAR}${TimeMaskDirective.TIME_EMPTY_CHAR}`;
  private dateValue: Date;
  private fieldJustGotFocus = false;
  private readonly hourCaretPositionBounderies = [0, Math.floor(TimeMaskDirective.TIME_MASK_LENGTH / 2)];
  private readonly minutesCaretPositionBounderies = [Math.floor(TimeMaskDirective.TIME_MASK_LENGTH / 2) + 1, TimeMaskDirective.TIME_MASK_LENGTH];

  constructor(
    @Self() private element: ElementRef,
    private renderer: Renderer2,
  ) {
    // Disable drag and drop
    this.renderer.listen(this.element.nativeElement, 'dragstart', this.disableDragDrop);
    this.renderer.listen(this.element.nativeElement, 'drop', this.disableDragDrop);
  }

  private disableDragDrop(event: Event): void {
    event.preventDefault();
  }

  /** Handles key events */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    switch (key) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Tab':
        this.handleArrowAndTabKeys(key, event);
        break;

      case 'Delete':
      case 'Backspace':
        this.clearHoursOrMinutes();
        break;

      default:
        if (key >= '0' && key <= '9') {
          this.handleNumberInput(key);
        }
    }

    if (key !== 'Tab') {
      event.preventDefault();
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    this.fieldJustGotFocus = true;
    this.setCaretPositionBasedOnClick();
  }

  @HostListener('focus', ['$event'])
  onFocus(event: any): void {
    this.fieldJustGotFocus = true;
    this.setCaretPositionBasedOnClick();
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    this._touched();
  }

  writeValue(value: Date): void {
    if (value && !(value instanceof Date)) {
      console.error('The appTimeMask directive requires the component value to be of type Date');
      return;
    }
    this.dateValue = new Date(value);
    const displayValue = value ? this.formatDateToString(value) : TimeMaskDirective.TIME_MASK;
    this.renderer.setProperty(this.element.nativeElement, 'value', displayValue);
  }

  registerOnChange(fn: (_: Date) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._touched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
  }

  validate(c: FormControl): Record<string, any> {
    return !(this.element.nativeElement as HTMLInputElement).value.includes(TimeMaskDirective.TIME_EMPTY_CHAR) ? null : { validTime: false };
  }

  private handleArrowAndTabKeys(key: string, event?: KeyboardEvent): void {
    const caretPosition = this.getCaretPosition();

    switch (key) {
      case 'ArrowRight':
        this.setCaretPosition(this.minutesCaretPositionBounderies[0], this.minutesCaretPositionBounderies[1]);
        break;
      case 'ArrowLeft':
        this.setCaretPosition(0, this.hourCaretPositionBounderies[1]);
        break;
      case 'Tab':
        if (caretPosition < this.hourCaretPositionBounderies[1] && !event.shiftKey) {
          this.setCaretPosition(this.minutesCaretPositionBounderies[0], this.minutesCaretPositionBounderies[1]);
          event.preventDefault();
        } else if (caretPosition > this.hourCaretPositionBounderies[1] && event.shiftKey) {
          this.setCaretPosition(0, this.hourCaretPositionBounderies[1]);
          event.preventDefault();
        }
    }
    this.fieldJustGotFocus = true;
  }

  private handleNumberInput(key: string): void {
    const input = (this.element.nativeElement as HTMLInputElement).value.split(TimeMaskDirective.TIME_SEPARATOR);
    const hours = input[0];
    const minutes = input[1];
    const caretPosition = this.getCaretPosition();
    if (caretPosition < this.minutesCaretPositionBounderies[0]) {
      this.updateHours(hours, minutes, key);
    } else {
      this.updateMinutes(hours, minutes, key);
    }
    this.fieldJustGotFocus = false;
  }

  private updateHours(hours: string, minutes: string, key: string): void {
    const newHour = this.calculateNewHour(hours, key);
    const completeTime = `${newHour}${TimeMaskDirective.TIME_SEPARATOR}${minutes}`;
    this.updateInputValue(completeTime);
    this.setCaretPositionBasedOnHour(newHour);
  }

  private updateMinutes(hours: string, minutes: string, key: string): void {
    const newMinutes = this.calculateNewMinutes(minutes, key);
    const completeTime = `${hours}${TimeMaskDirective.TIME_SEPARATOR}${newMinutes}`;
    this.updateInputValue(completeTime);
    this.setCaretPosition(this.minutesCaretPositionBounderies[0], this.minutesCaretPositionBounderies[1]);
  }

  private calculateNewHour(hours: string, key: string): string {
    const firstDigit = hours[0];
    const secondDigit = hours[1];
    const MAX_HOUR = 23;
    if (firstDigit === TimeMaskDirective.TIME_EMPTY_CHAR || this.fieldJustGotFocus) {
      return `0${key}`;
    } else {
      let newHour = `${secondDigit}${key}`;
      if (Number(newHour) > MAX_HOUR) {
        newHour = MAX_HOUR.toString();
      }
      return newHour;
    }
  }

  private calculateNewMinutes(minutes: string, key: string): string {
    const firstDigit = minutes[0];
    const secondDigit = minutes[1];
    if (firstDigit === TimeMaskDirective.TIME_EMPTY_CHAR || this.fieldJustGotFocus) {
      return `0${key}`;
    } else {
      let newMinutes = `${secondDigit}${key}`;
      if (Number(newMinutes) > 59) {
        newMinutes = '59';
      }
      return newMinutes;
    }
  }

  private updateInputValue(value: string): void {
    this.renderer.setProperty(this.element.nativeElement, 'value', value);
    this.controlValueChanged();
  }

  private setCaretPositionBasedOnHour(newHour: string): void {
    if (Number(newHour) > this.hourCaretPositionBounderies[1]) {
      this.setCaretPosition(this.minutesCaretPositionBounderies[0], this.minutesCaretPositionBounderies[1]);
      this.fieldJustGotFocus = true;
    } else {
      this.setCaretPosition(0, this.hourCaretPositionBounderies[1]);
    }
  }

  private clearHoursOrMinutes(): void {
    const caretPosition = this.getCaretPosition();
    const input = (this.element.nativeElement as HTMLInputElement).value.split(TimeMaskDirective.TIME_SEPARATOR);
    const hours = input[0];
    const minutes = input[1];
    const newTime = caretPosition > this.hourCaretPositionBounderies[1] ? `${hours}${TimeMaskDirective.TIME_SEPARATOR}--` : `--${TimeMaskDirective.TIME_SEPARATOR}${minutes}`;
    this.updateInputValue(newTime);
    this.setCaretPositionBasedOnClear(caretPosition);
  }

  private setCaretPositionBasedOnClear(caretPosition: number): void {
    if (caretPosition > this.hourCaretPositionBounderies[1]) {
      this.setCaretPosition(this.minutesCaretPositionBounderies[0], this.minutesCaretPositionBounderies[1]);
    } else {
      this.setCaretPosition(0, this.hourCaretPositionBounderies[1]);
    }
    this.fieldJustGotFocus = true;
  }

  private setCaretPositionBasedOnClick(): void {
    const caretPosition = this.getCaretPosition();
    if (caretPosition < this.minutesCaretPositionBounderies[0]) {
      this.setCaretPosition(0, this.hourCaretPositionBounderies[1]);
    } else {
      this.setCaretPosition(this.minutesCaretPositionBounderies[0], this.minutesCaretPositionBounderies[1]);
    }
  }

  private getCaretPosition(): number {
    const nativeElement = this.element.nativeElement as HTMLInputElement;
    nativeElement.focus();
    return nativeElement.selectionStart;
  }

  private setCaretPosition(start: number, end: number): void {
    (this.element.nativeElement as HTMLInputElement).setSelectionRange(start, end);
  }

  private formatDateToString(value: Date): string {
    return this.zeroFill(value.getHours()) + TimeMaskDirective.TIME_SEPARATOR + this.zeroFill(value.getMinutes());
  }

  private zeroFill(value: number): string {
    return (value > 9 ? '' : '0') + value;
  }

  private controlValueChanged(): void {
    const timeArray = (this.element.nativeElement as HTMLInputElement).value.split(TimeMaskDirective.TIME_SEPARATOR);

    this.dateValue = new Date(this.dateValue.setHours(this.stringToNumber(timeArray[0])));
    this.dateValue = new Date(this.dateValue.setMinutes(this.stringToNumber(timeArray[1])));
    this._onChange(this.dateValue);
  }

  private stringToNumber(str: string): number {
    return Number(str.replace(/-/g, '0'));
  }
}
